import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, GripVertical, Zap } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Checklist } from "@/hooks/useChecklists";
import {
  useChecklistRules,
  useCreateChecklistRule,
  useUpdateChecklistRule,
  useDeleteChecklistRule,
  RuleType,
  RuleValue,
  StayLengthValue,
  GapDaysValue,
  DayOfWeekValue,
} from "@/hooks/useChecklistRules";

interface ChecklistRulesSectionProps {
  propertyId: string;
  checklists: Checklist[];
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  stay_length: "Stay Length",
  same_day_turnover: "Same-Day Turnover",
  gap_days: "Gap Days Between Bookings",
  day_of_week: "Day of Week",
};

const ChecklistRulesSection = ({ propertyId, checklists }: ChecklistRulesSectionProps) => {
  const { data: rules, isLoading } = useChecklistRules(propertyId);
  const createRule = useCreateChecklistRule();
  const updateRule = useUpdateChecklistRule();
  const deleteRule = useDeleteChecklistRule();

  const [isAdding, setIsAdding] = useState(false);
  const [newChecklistId, setNewChecklistId] = useState("");
  const [newRuleType, setNewRuleType] = useState<RuleType | "">("");
  const [newRuleValue, setNewRuleValue] = useState<Partial<RuleValue>>({});

  const resetForm = () => {
    setIsAdding(false);
    setNewChecklistId("");
    setNewRuleType("");
    setNewRuleValue({});
  };

  const handleAddRule = async () => {
    if (!newChecklistId || !newRuleType) {
      toast({ title: "Error", description: "Please select a checklist and rule type", variant: "destructive" });
      return;
    }

    let ruleValue: RuleValue;

    switch (newRuleType) {
      case "stay_length":
        const stayValue = newRuleValue as Partial<StayLengthValue>;
        if (!stayValue.min_nights || !stayValue.max_nights) {
          toast({ title: "Error", description: "Please enter min and max nights", variant: "destructive" });
          return;
        }
        ruleValue = { min_nights: Number(stayValue.min_nights), max_nights: Number(stayValue.max_nights) };
        break;
      case "same_day_turnover":
        ruleValue = { enabled: true };
        break;
      case "gap_days":
        const gapValue = newRuleValue as Partial<GapDaysValue>;
        if (gapValue.gap_days === undefined) {
          toast({ title: "Error", description: "Please enter gap days", variant: "destructive" });
          return;
        }
        ruleValue = { gap_days: Number(gapValue.gap_days) };
        break;
      case "day_of_week":
        const dayValue = newRuleValue as Partial<DayOfWeekValue>;
        if (!dayValue.weekdays || dayValue.weekdays.length === 0) {
          toast({ title: "Error", description: "Please select at least one day", variant: "destructive" });
          return;
        }
        ruleValue = { weekdays: dayValue.weekdays };
        break;
      default:
        return;
    }

    try {
      await createRule.mutateAsync({
        propertyId,
        checklistId: newChecklistId,
        ruleType: newRuleType,
        ruleValue,
      });
      toast({ title: "Success", description: "Rule created" });
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create rule", variant: "destructive" });
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await deleteRule.mutateAsync({ id: ruleId, propertyId });
      toast({ title: "Success", description: "Rule deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete rule", variant: "destructive" });
    }
  };

  const getChecklistName = (checklistId: string) => {
    return checklists.find((c) => c.id === checklistId)?.name || "Unknown";
  };

  const formatRuleValue = (ruleType: RuleType, value: RuleValue) => {
    switch (ruleType) {
      case "stay_length":
        const stay = value as StayLengthValue;
        return `${stay.min_nights}-${stay.max_nights} nights`;
      case "same_day_turnover":
        return "Checkout = Check-in same day";
      case "gap_days":
        const gap = value as GapDaysValue;
        return gap.gap_days === 0 ? "No gap between bookings" : `${gap.gap_days} day gap`;
      case "day_of_week":
        const day = value as DayOfWeekValue;
        return day.weekdays.join(", ");
      default:
        return "";
    }
  };

  const toggleWeekday = (day: string) => {
    const current = (newRuleValue as Partial<DayOfWeekValue>).weekdays || [];
    if (current.includes(day)) {
      setNewRuleValue({ weekdays: current.filter((d) => d !== day) });
    } else {
      setNewRuleValue({ weekdays: [...current, day] });
    }
  };

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading rules...</p>;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          <CardTitle className="text-base">Checklist Automation Rules</CardTitle>
        </div>
        <CardDescription>
          Rules are evaluated top to bottom when generating cleaning tasks. First matching rule wins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing Rules */}
        {rules && rules.length > 0 ? (
          <div className="space-y-2">
            {rules.map((rule, index) => (
              <div
                key={rule.id}
                className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
              >
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground w-6">{index + 1}.</span>
                <div className="flex-1 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" className="font-medium">
                    {getChecklistName(rule.checklist_id)}
                  </Badge>
                  <span className="text-sm text-muted-foreground">when</span>
                  <Badge variant="outline">{RULE_TYPE_LABELS[rule.rule_type]}</Badge>
                  <span className="text-sm">{formatRuleValue(rule.rule_type, rule.rule_value)}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteRule(rule.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-2">
            No automation rules yet. Add rules to automatically assign checklists based on booking conditions.
          </p>
        )}

        {/* Add New Rule Form */}
        {isAdding ? (
          <Card className="border-dashed">
            <CardContent className="pt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Assign Checklist</Label>
                  <Select value={newChecklistId} onValueChange={setNewChecklistId}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select checklist..." />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50">
                      {checklists.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>When Condition</Label>
                  <Select value={newRuleType} onValueChange={(v) => { setNewRuleType(v as RuleType); setNewRuleValue({}); }}>
                    <SelectTrigger className="bg-background">
                      <SelectValue placeholder="Select rule type..." />
                    </SelectTrigger>
                    <SelectContent className="bg-popover z-50">
                      <SelectItem value="stay_length">Stay Length</SelectItem>
                      <SelectItem value="same_day_turnover">Same-Day Turnover</SelectItem>
                      <SelectItem value="gap_days">Gap Days Between Bookings</SelectItem>
                      <SelectItem value="day_of_week">Day of Week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rule-specific inputs */}
              {newRuleType === "stay_length" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Min Nights</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="1"
                      value={(newRuleValue as Partial<StayLengthValue>).min_nights || ""}
                      onChange={(e) => setNewRuleValue({ ...newRuleValue, min_nights: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Nights</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="3"
                      value={(newRuleValue as Partial<StayLengthValue>).max_nights || ""}
                      onChange={(e) => setNewRuleValue({ ...newRuleValue, max_nights: Number(e.target.value) })}
                    />
                  </div>
                </div>
              )}

              {newRuleType === "same_day_turnover" && (
                <p className="text-sm text-muted-foreground">
                  This rule applies when the checkout date equals another booking's check-in date.
                </p>
              )}

              {newRuleType === "gap_days" && (
                <div className="space-y-2">
                  <Label>Gap Days (0 = no gap)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={(newRuleValue as Partial<GapDaysValue>).gap_days ?? ""}
                    onChange={(e) => setNewRuleValue({ gap_days: Number(e.target.value) })}
                  />
                </div>
              )}

              {newRuleType === "day_of_week" && (
                <div className="space-y-2">
                  <Label>Select Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((day) => {
                      const selected = ((newRuleValue as Partial<DayOfWeekValue>).weekdays || []).includes(day);
                      return (
                        <Button
                          key={day}
                          type="button"
                          variant={selected ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleWeekday(day)}
                        >
                          {day.slice(0, 3)}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={handleAddRule} disabled={createRule.isPending}>
                  {createRule.isPending ? "Adding..." : "Add Rule"}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" onClick={() => setIsAdding(true)} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Automation Rule
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ChecklistRulesSection;
